import { LoadingSpinner } from '../shared/components/LoadingSpinner';
import { Formatters } from '../shared/lib/formatters';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';

// Impact — the RM earnings ledger (docs/redesign/04 §1).
//
// "A tracker is a table of actions, not a poster of totals." One row per
// action with a lifecycle status; the stat strip on top is the summary of
// the rows below it — never a separately computed number.
//
// KILLED per spec: the gradient hero, Beating-the-Market (portfolio health,
// lives on Today/Health), Actioned-Gaps / Flag-Activity / Action-Breakdown
// cards (all became ledger rows or strip sub-lines).
export class ImpactPage {
  private container: HTMLElement;
  private data: any = null;
  private statusFilter = 'all';
  private propertyFilter = '';

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="page-content">
        <div id="ip-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>
      </div>`;
    try {
      const r = await fetch('/api/impact');
      this.data = r.ok ? await r.json() : null;
    } catch (_) { this.data = null; }
    this.draw();
  }

  private money(v: any): string { return '$' + Math.round(Number(v) || 0).toLocaleString(); }

  private statusOf(row: any): { key: string; label: string; bg: string; fg: string; pill: string } {
    if (row.status === 'pending') return { key: 'pending', label: 'Pending', bg: 'var(--bg-subtle)', fg: 'var(--text-muted)', pill: 'pill-gray' };
    if (row.type === 'dismiss') {
      return row.status === 'landed'
        ? { key: 'false_alarm', label: 'False alarm', bg: 'var(--bg-subtle)', fg: 'var(--text-muted)', pill: 'pill-gray' }
        : { key: 'logged', label: 'Skipped — right call', bg: 'var(--bg-subtle)', fg: 'var(--text-muted)', pill: 'pill-gray' };
    }
    if (row.type === 'manual_adjust') return { key: 'logged', label: 'Adjusted', bg: 'var(--info-soft)', fg: 'var(--info-text)', pill: 'pill-blue' };
    if (row.status === 'landed') return { key: 'captured', label: `Captured${row.amount ? ' +' + this.money(row.amount) : ''}`, bg: 'var(--success-soft)', fg: 'var(--success-text)', pill: 'pill-green' };
    return { key: 'no_booking', label: 'No booking', bg: 'var(--warning-soft)', fg: 'var(--warning-text)', pill: 'pill-amber' };
  }

  private sentenceOf(row: any): string {
    const stay = new Date(row.stay_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (row.type === 'manual_adjust') return `Adjusted ${stay}${row.lever ? ' — ' + String(row.lever).replace(/_/g, ' ') : ''}`;
    if (row.type === 'dismiss') return `Dismissed ${stay}`;
    return `Accepted ${stay}${row.lever ? ' · ' + String(row.lever).replace(/_/g, ' ') : ''}${row.pushed_at ? ' · pushed' : ' · staged'}`;
  }

  private draw(): void {
    const body = document.getElementById('ip-body');
    if (!body) return;
    const d = this.data;
    if (!d) { body.innerHTML = emptyState('Could not load the ledger', 'Refresh; if it persists the sync may be running.', '⚠️'); return; }
    const ledger: any[] = d.ledger || [];

    // Strip numbers ARE summaries of the rows (PRINCIPLES.md #2).
    const captured = d.captured_revenue ?? 0;
    const pending = d.pending_revenue ?? 0;
    const realizedAccepts = ledger.filter(r => r.type === 'accept' && r.status !== 'pending');
    const landed = realizedAccepts.filter(r => r.status === 'landed').length;
    const falseAlarms = d.dismissed_false_alarm ?? 0;
    // 30d sub-line counted from the ledger itself so the cell has ONE window
    const cutoff = Date.now() - 30 * 86400000;
    const recent = ledger.filter(r => r.at && new Date(r.at).getTime() >= cutoff);
    const a30 = {
      accept: recent.filter(r => r.type === 'accept').length,
      adjust: recent.filter(r => r.type === 'manual_adjust').length,
      dismiss: recent.filter(r => r.type === 'dismiss').length,
    };
    const a30total = Number(d.actions_30d) || 0;

    const rows = ledger
      .filter(r => this.propertyFilter ? String(r.listing_id) === this.propertyFilter : true)
      .filter(r => this.statusFilter === 'all' ? true : this.statusOf(r).key === this.statusFilter);

    const properties = [...new Map(ledger.map(r => [String(r.listing_id), r.name])).entries()];

    const chip = (key: string, label: string) =>
      `<button data-ip-status="${key}" style="font-size:11px; padding:3px 10px; border-radius:999px; cursor:pointer; border:1px solid ${this.statusFilter === key ? 'var(--text)' : 'var(--border)'}; background:${this.statusFilter === key ? 'var(--text)' : '#fff'}; color:${this.statusFilter === key ? '#fff' : 'var(--text-muted)'};">${label}</button>`;

    body.innerHTML = `
      <style>
        .ip-filters { display:flex; gap:6px; align-items:center; margin:18px 0 12px; flex-wrap:wrap; }
        .ip-row { cursor:default; transition:background 0.12s ease; }
        .ip-row:hover { background:var(--bg-subtle); }
        .ip-prop-link { color:var(--info); cursor:pointer; font-weight:500; }
        .ip-prop-link:hover { text-decoration:underline; }
      </style>
      <div class="dash-head">
        <h1>Impact — the RM earnings ledger</h1>
        <p>one row per action, attributed only after the stay date passes — never invented</p>
      </div>
      <div class="kpi-section-label">REVENUE</div>
      <div class="kpi-grid">
        <div class="kpi-card tone-good">
          <div class="kpi-card-top"><span class="kpi-card-label">Captured revenue</span><span class="kpi-card-ic">${icon('wallet', 17)}</span></div>
          <div class="kpi-card-value mono num">${this.money(captured)}</div>
          <div class="kpi-card-cap">risk-adjusted, stays passed</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Pending</span><span class="kpi-card-ic">${icon('receipt', 17)}</span></div>
          <div class="kpi-card-value mono num">${this.money(pending)}</div>
          <div class="kpi-card-cap">accepted, stay date ahead</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Hit rate</span><span class="kpi-card-ic">${icon('target', 17)}</span></div>
          <div class="kpi-card-value mono num">${realizedAccepts.length ? `${landed}/${realizedAccepts.length} · ${Math.round(landed / realizedAccepts.length * 100)}%` : '—'}</div>
          <div class="kpi-card-cap">realized accepts that booked</div>
        </div>
        <div class="kpi-card${falseAlarms ? ' tone-bad' : ''}">
          <div class="kpi-card-top"><span class="kpi-card-label">False alarms</span><span class="kpi-card-ic">${icon('flag', 17)}</span></div>
          <div class="kpi-card-value mono num">${falseAlarms}</div>
          <div class="kpi-card-cap">flagged, you skipped, booked anyway</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Actions · 30d</span><span class="kpi-card-ic">${icon('pulse', 17)}</span></div>
          <div class="kpi-card-value mono num">${a30total}</div>
          <div class="kpi-card-cap mono num">${a30.accept} accepts · ${a30.adjust} adjusts · ${a30.dismiss} dismisses</div>
        </div>
      </div>
      <div class="ip-filters">
        ${chip('all', 'All')}${chip('captured', 'Captured')}${chip('pending', 'Pending')}${chip('no_booking', 'No booking')}${chip('false_alarm', 'False alarms')}${chip('logged', 'Adjusted / skipped')}
        <select id="ip-prop" style="margin-left:auto; font-size:12px; padding:4px 8px; border:1px solid var(--border); border-radius:6px;">
          <option value="">All properties</option>
          ${properties.map(([id, name]) => `<option value="${id}" ${this.propertyFilter === id ? 'selected' : ''}>${Formatters.escapeHtml(name || id)}</option>`).join('')}
        </select>
      </div>
      ${rows.length ? `
      <div class="table-scroll" style="border:1px solid var(--border); border-radius:var(--radius);">
        <table>
          <thead><tr><th>Date</th><th>Property</th><th>Action</th><th>Status</th><th style="text-align:right;">Attributed $</th></tr></thead>
          <tbody>
            ${rows.map(r => {
              const s = this.statusOf(r);
              return `<tr class="ip-row">
                <td class="mono num">${r.at ? new Date(r.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</td>
                <td><span class="ip-prop-link" data-ip-open="${r.listing_id}" title="Open this property's calendar">${Formatters.escapeHtml(r.name || r.listing_id)}</span></td>
                <td>${this.sentenceOf(r)}</td>
                <td><span class="pill ${s.pill}">${s.label}</span></td>
                <td class="mono num" style="text-align:right;">${r.amount != null && s.key === 'captured' ? '+' + this.money(r.amount) : r.amount != null && s.key === 'pending' ? this.money(r.amount) : '—'}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>`
      : emptyState('No actions yet', 'Accept a price change or log an adjustment on a property calendar — every action lands here with its lifecycle.', '📒')}
    `;
    this.bind();
  }

  private bind(): void {
    this.container.querySelectorAll<HTMLElement>('[data-ip-status]').forEach(b =>
      b.addEventListener('click', () => { this.statusFilter = b.dataset.ipStatus!; this.draw(); }));
    document.getElementById('ip-prop')?.addEventListener('change', (e) => {
      this.propertyFilter = (e.target as HTMLSelectElement).value;
      this.draw();
    });
    this.container.querySelectorAll<HTMLElement>('[data-ip-open]').forEach(el =>
      el.addEventListener('click', () => {
        sessionStorage.setItem('workup_preselect', el.dataset.ipOpen!);
        sessionStorage.setItem('workup_tab', 'propensity');
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'data-explorer' }));
      }));
  }
}
